import { useEffect, useMemo, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { resolveAdminMediaUrl } from '@formosa/shared/utils/mediaUrl';
import DualListTransfer from '../../components/DualListTransfer';
import FrontConfigEditModal from './FrontConfigEditModal';
import {
  FRONT_CONFIG_TABS,
  GLOBAL_CONFIG_KEY,
  GLOBAL_WALLET_TYPE_OPTIONS,
  KYC_CONFIG_KEY,
  KYC_FIELD_OPTIONS,
  LOGO_CONFIG_KEY,
  REGISTER_CONFIG_KEY,
  TIME_ZONE_CONFIG_KEY,
  VERIFY_CODE_CONFIG_KEY,
  VERIFY_CODE_OPTIONS,
  TEST_MEMBER_WITHDRAW_OPTIONS,
  DATE_FORMAT_OPTIONS,
  TIME_FORMAT_OPTIONS,
  buildCountryItems,
  buildCountrySavePayload,
  buildLanguageItems,
  buildLanguageSavePayload,
  formatBoolLabel,
  formatDateFormatLabel,
  formatGlobalWalletTypeLabels,
  formatTimeFormatLabel,
  formatVerifyCodeModeLabel,
  formatYesNo,
  globalContentToForm,
  globalFormToContent,
  kycFormToContent,
  kycPaymentContentToForm,
  logoContentToForm,
  logoFormToContent,
  paymentSettingFormToContent,
  registerContentToForm,
  registerFormToContent,
  resolveEnabledCountryKeys,
  resolveEnabledLanguageKeys,
  timeZoneContentToForm,
  timeZoneFormToContent,
  verifyCodeContentToForm,
  verifyCodeFormToContent,
} from './frontConfigConstants';

function BoolRadio({ label, value, onChange, yesText = '是', noText = '否' }) {
  return (
    <div className="form-field form-field--full">
      <span className="form-field__label">{label}</span>
      <div className="product-type-radios">
        <label className="checkbox-inline">
          <input type="radio" checked={value === true} onChange={() => onChange(true)} />
          {yesText}
        </label>
        <label className="checkbox-inline">
          <input type="radio" checked={value === false} onChange={() => onChange(false)} />
          {noText}
        </label>
      </div>
    </div>
  );
}

function SwitchRadio({ label, value, onChange }) {
  return BoolRadio({ label, value, onChange, yesText: '開', noText: '關' });
}

export default function FrontConfigPage() {
  const [activeTab, setActiveTab] = useState('language');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [allLanguages, setAllLanguages] = useState([]);
  const [allCountries, setAllCountries] = useState([]);
  const [registerContent, setRegisterContent] = useState({});
  const [kycContent, setKycContent] = useState({});
  const [paymentContent, setPaymentContent] = useState({});
  const [verifyCodeContent, setVerifyCodeContent] = useState({});
  const [logoContent, setLogoContent] = useState({});
  const [timeZoneContent, setTimeZoneContent] = useState({});
  const [globalContent, setGlobalContent] = useState({});

  const [editOpen, setEditOpen] = useState(false);
  const [languageForm, setLanguageForm] = useState({ enabledKeys: [] });
  const [registerForm, setRegisterForm] = useState(registerContentToForm({}));
  const [kycPaymentForm, setKycPaymentForm] = useState(kycPaymentContentToForm({}, {}));
  const [verifyCodeForm, setVerifyCodeForm] = useState(verifyCodeContentToForm({}));
  const [logoForm, setLogoForm] = useState(logoContentToForm({}));
  const [timeZoneForm, setTimeZoneForm] = useState(timeZoneContentToForm({}));
  const [globalForm, setGlobalForm] = useState(globalContentToForm({}));
  const [uploadingField, setUploadingField] = useState('');

  const languageItems = useMemo(() => buildLanguageItems(allLanguages), [allLanguages]);
  const enabledCountryKeys = useMemo(
    () => resolveEnabledCountryKeys(allCountries),
    [allCountries]
  );
  const countryItems = useMemo(() => buildCountryItems(allCountries), [allCountries]);
  const enabledLanguageKeys = useMemo(
    () => resolveEnabledLanguageKeys(registerContent, allLanguages),
    [registerContent, allLanguages]
  );
  const enabledLanguageLabels = useMemo(
    () => enabledLanguageKeys
      .map((key) => languageItems.find((item) => item.key === key)?.label || key),
    [enabledLanguageKeys, languageItems]
  );

  async function loadAll() {
    setLoading(true);
    setError('');
    try {
      const [
        languagePage,
        countries,
        registerRes,
        kycRes,
        paymentRes,
        verifyRes,
        logoRes,
        timeZoneRes,
        globalRes,
      ] = await Promise.all([
        adminApi.listAllLanguages(),
        adminApi.listAllCountries(true),
        adminApi.tenantConfigOne(REGISTER_CONFIG_KEY),
        adminApi.tenantConfigOne(KYC_CONFIG_KEY),
        adminApi.getPaymentSettingConfig(),
        adminApi.tenantConfigOne(VERIFY_CODE_CONFIG_KEY),
        adminApi.tenantConfigOne(LOGO_CONFIG_KEY),
        adminApi.tenantConfigOne(TIME_ZONE_CONFIG_KEY),
        adminApi.tenantConfigOne(GLOBAL_CONFIG_KEY),
      ]);
      const languages = languagePage?.records || [];
      setAllLanguages(languages);
      setAllCountries(countries || []);
      setRegisterContent(registerRes?.content || {});
      setKycContent(kycRes?.content || {});
      setPaymentContent(paymentRes?.content || {});
      setVerifyCodeContent(verifyRes?.content || {});
      setLogoContent(logoRes?.content || {});
      setTimeZoneContent(timeZoneRes?.content || {});
      setGlobalContent(globalRes?.content || {});
    } catch (err) {
      setError(err.message || '載入失敗');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAll();
  }, []);

  function openEdit() {
    setMessage('');
    setLanguageForm({ enabledKeys: [...enabledLanguageKeys] });
    setRegisterForm(registerContentToForm(registerContent, allCountries));
    setKycPaymentForm(kycPaymentContentToForm(kycContent, paymentContent));
    setVerifyCodeForm(verifyCodeContentToForm(verifyCodeContent));
    setLogoForm(logoContentToForm(logoContent));
    setTimeZoneForm(timeZoneContentToForm(timeZoneContent));
    setGlobalForm(globalContentToForm(globalContent));
    setEditOpen(true);
  }

  async function saveLanguageTab() {
    const { languages, updates } = await buildLanguageSavePayload(
      languageForm.enabledKeys,
      allLanguages
    );
    await adminApi.saveTenantConfig(REGISTER_CONFIG_KEY, { languages });
    await Promise.all(
      updates.filter((item) => item.id).map((item) => adminApi.updateLanguage(item))
    );
  }

  async function saveRegisterTab() {
    const { enNames, updates } = buildCountrySavePayload(
      registerForm.enabledCountryKeys,
      allCountries
    );
    await Promise.all(updates.map((item) => adminApi.updateCountry(item)));
    await adminApi.saveTenantConfig(
      REGISTER_CONFIG_KEY,
      registerFormToContent({
        ...registerForm,
        enNames,
        languages: registerContent.languages || enabledLanguageKeys,
        verifyCode: registerContent.verifyCode,
      })
    );
  }

  async function handleSubmit() {
    setSubmitting(true);
    setError('');
    setMessage('');
    try {
      if (activeTab === 'language') {
        await saveLanguageTab();
      } else if (activeTab === 'register') {
        await saveRegisterTab();
      } else if (activeTab === 'kycPayment') {
        await adminApi.saveTenantConfig(KYC_CONFIG_KEY, kycFormToContent(kycPaymentForm));
        await adminApi.savePaymentSettingConfig(
          paymentSettingFormToContent(kycPaymentForm.payment)
        );
      } else if (activeTab === 'verifyCode') {
        await adminApi.saveTenantConfig(
          VERIFY_CODE_CONFIG_KEY,
          verifyCodeFormToContent(verifyCodeForm)
        );
      } else if (activeTab === 'logo') {
        await adminApi.saveTenantConfig(LOGO_CONFIG_KEY, logoFormToContent(logoForm));
      } else if (activeTab === 'timeZone') {
        await adminApi.saveTenantConfig(TIME_ZONE_CONFIG_KEY, timeZoneFormToContent(timeZoneForm));
      } else if (activeTab === 'global') {
        await adminApi.saveTenantConfig(GLOBAL_CONFIG_KEY, globalFormToContent(globalForm));
      }
      setEditOpen(false);
      setMessage('已保存');
      await loadAll();
    } catch (err) {
      setError(err.message || '保存失敗');
    } finally {
      setSubmitting(false);
    }
  }

  function handleReset() {
    setLanguageForm({ enabledKeys: [...enabledLanguageKeys] });
    setRegisterForm(registerContentToForm(registerContent, allCountries));
    setKycPaymentForm(kycPaymentContentToForm(kycContent, paymentContent));
    setVerifyCodeForm(verifyCodeContentToForm(verifyCodeContent));
    setLogoForm(logoContentToForm(logoContent));
    setTimeZoneForm(timeZoneContentToForm(timeZoneContent));
    setGlobalForm(globalContentToForm(globalContent));
  }

  async function handleLogoUpload(field, event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    setUploadingField(field);
    try {
      const url = await adminApi.uploadOssImage(file);
      setLogoForm((prev) => ({ ...prev, [field]: url || '' }));
    } catch (err) {
      setError(err.message || '上傳失敗');
    } finally {
      setUploadingField('');
    }
  }

  function renderLanguageView() {
    return (
      <div className="front-config-view">
        <div className="front-config-view__row">
          <span className="front-config-view__label">前台語系：</span>
          <ol className="front-config-view__list">
            {enabledLanguageLabels.map((label) => (
              <li key={label}>{label}</li>
            ))}
          </ol>
        </div>
        <p className="front-config-hint">＊排序第一位為預設值</p>
      </div>
    );
  }

  function renderRegisterView() {
    const form = registerContentToForm(registerContent, allCountries);
    const countryLabels = enabledCountryKeys
      .map((key) => countryItems.find((item) => item.key === key)?.label || key);
    return (
      <div className="front-config-view">
        <p>未登入只可以看到註冊/登入頁：{formatBoolLabel(form.showOnlyAuthPage)}</p>
        <p>登入/註冊頁是否顯示切換語系 UI：{formatBoolLabel(form.showLanguageSwitchUI)}</p>
        <p>註冊成功時是否綁定試玩帳號：{formatYesNo(form.bindTouristAccountOnRegister)}</p>
        <h4>登入設置</h4>
        <p>登入方式：{[form.emailLogin && '信箱', form.phoneLogin && '手機號碼'].filter(Boolean).join('、') || '—'}</p>
        <p>登入頁面是否顯示忘記密碼：{formatYesNo(form.forgotPassword)}</p>
        <h4>註冊設置</h4>
        <p>註冊方式：{[form.emailRegister && '信箱', form.phoneRegister && '手機號碼'].filter(Boolean).join('、') || '—'}</p>
        <p>註冊需要邀請碼：{formatBoolLabel(form.needPromotionCode)}</p>
        <p>註冊需顯示交易密碼：{formatBoolLabel(form.registerTradePassword)}</p>
        <div className="front-config-view__row">
          <span className="front-config-view__label">可註冊/登入的國碼：</span>
          <ol className="front-config-view__list">
            {countryLabels.map((label) => (
              <li key={label}>{label}</li>
            ))}
          </ol>
        </div>
        <p>遊客試玩開關：{formatBoolLabel(form.tourists)}</p>
        <p>
          註冊成功是否贈送彩金：
          {form.sendBonusOnRegister ? `是，彩金 ${form.sendBonusWithRegister}` : '否'}
        </p>
      </div>
    );
  }

  function renderKycPaymentView() {
    const form = kycPaymentContentToForm(kycContent, paymentContent);
    const kycLabels = (form.usedList || [])
      .map((value) => KYC_FIELD_OPTIONS.find((item) => item.value === value)?.label || value);
    const withdrawOption = TEST_MEMBER_WITHDRAW_OPTIONS.find((item) => item.value === form.testMemberWithdraw);
    return (
      <div className="front-config-view">
        <p>認證資訊選項：{kycLabels.join('、') || '—'}</p>
        <p>提現是否須 KYC：{formatYesNo(form.withdrawNeedKyc)}</p>
        <p>綁定地址是否需要 KYC：{formatYesNo(form.bindWithdrawAddressNeedKyc)}</p>
        <p>尚有未審核訂單，不得提現：{formatBoolLabel(form.blockWithdrawWhenPending)}</p>
        <p>提現閥值開關：{formatBoolLabel(form.withdrawalThresholdSwitch)}</p>
        <p>是否充值自動鎖倉：{formatYesNo(form.depositAutoLock)}</p>
        <p>測試會員是否開放前台提現及自動審核：{withdrawOption?.label || '—'}</p>
        <p>測試會員是否開放前台存款自動審核：{formatYesNo(form.autoApproveTestMemberDeposit)}</p>
        <p>首次取款是否檢核充值達到指定金額：{formatYesNo(form.checkFirstWithdrawWalletRange)}</p>
      </div>
    );
  }

  function renderVerifyCodeView() {
    const form = verifyCodeContentToForm(verifyCodeContent);
    return (
      <div className="front-config-view">
        <p className="front-config-view__label">是否啟用驗證碼：</p>
        <ul className="front-config-view__list front-config-view__list--plain">
          {VERIFY_CODE_OPTIONS.map((option) => (
            <li key={option.key}>
              {option.label}：{formatVerifyCodeModeLabel(form.modes?.[option.key])}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  function renderLogoView() {
    const form = logoContentToForm(logoContent);
    return (
      <div className="front-config-view">
        <p>前台站台名稱：{form.tenantName || '—'}</p>
        <p>後台站台名稱：{form.backendSiteName || '—'}</p>
        {form.icon ? <img src={resolveAdminMediaUrl(form.icon)} alt="" className="front-config-logo-preview" /> : null}
        {form.banner ? <img src={resolveAdminMediaUrl(form.banner)} alt="" className="front-config-logo-preview front-config-logo-preview--lg" /> : null}
        {form.email ? <img src={resolveAdminMediaUrl(form.email)} alt="" className="front-config-logo-preview front-config-logo-preview--wide" /> : null}
        {form.backendSiteIcon ? <img src={resolveAdminMediaUrl(form.backendSiteIcon)} alt="" className="front-config-logo-preview" /> : null}
      </div>
    );
  }

  function renderTimeZoneView() {
    const form = timeZoneContentToForm(timeZoneContent);
    return (
      <div className="front-config-view">
        <p>時區：{form.timeZone || '—'}</p>
        <p className="front-config-hint front-config-hint--danger">
          ＊開站時確認，不得更動，避免開獎和資料出現問題
        </p>
        <p>是否跟隨客戶端時區：{formatYesNo(form.useBrowserTimezone)}</p>
        <p>日期格式：{formatDateFormatLabel(form.dateFormat)}</p>
        <p>時間格式：{formatTimeFormatLabel(form.timeFormat)}</p>
      </div>
    );
  }

  function renderGlobalView() {
    const form = globalContentToForm(globalContent);
    return (
      <div className="front-config-view">
        <p>免密登入前台域名：{form.h5Url || '—'}</p>
        <p>全局投注頁是否顯示賠率：{formatYesNo(form.showBettingOdds)}</p>
        <p>明細漲跌符號是否顯示：{formatYesNo(form.showDetailUpDownSign)}</p>
        <p>持倉明細是否顯示日期與現價：{formatYesNo(form.showPositionDetailDateAndPrice)}</p>
        <p>
          是否限制餘額大於指定金額才可投注：
          {form.betAmountSwitch ? `是，需 ≥ ${form.betAmount}` : '否'}
        </p>
        <p>登入、註冊、語言設定頁是否顯示國家國旗：{formatYesNo(form.showCountryFlag)}</p>
        <p>是否開放前台交割合約、永續合約撤單功能：{formatYesNo(form.enableContractCancel)}</p>
        <p>
          交割合約當日投注次數大於等於 {form.deliveriesNumber || '—'}，
          用戶餘額百分比 {form.deliveriesAmountProportion || '—'} %，視為違規利潤
        </p>
        <p>投注頁是否可以手動輸入金額：{formatYesNo(form.allowManualAmountInput)}</p>
        <p>不同站點註冊帳號是否允許重複：{formatYesNo(form.checkUsernameRegisterDomainEnable)}</p>
        <p>資金明細卡片顯示項：{formatGlobalWalletTypeLabels(form.globalWalletTypes)}</p>
        <p>首頁是否顯示活動專區懸浮 icon：{formatBoolLabel(form.showActivityIcon)}</p>
        <p>
          是否開啟跟單頁鎖倉金額限制：
          {form.followOrderLockedBalanceRestrictOpen
            ? `是，低於 ${form.followOrderLockedBalanceRestrictAmount}`
            : '否，跟單不需要判斷鎖倉'}
        </p>
      </div>
    );
  }

  function renderConditionalAmountRadio({
    label,
    enabled,
    amount,
    onEnabledChange,
    onAmountChange,
  }) {
    return (
      <div className="form-field form-field--full">
        <span className="form-field__label">{label}</span>
        <div className="product-type-radios">
          <label className="checkbox-inline">
            <input
              type="radio"
              checked={enabled}
              onChange={() => onEnabledChange(true)}
            />
            是
            {enabled ? (
              <input
                type="number"
                min="0"
                step="any"
                className="inline-number-input"
                value={amount}
                onChange={(e) => onAmountChange(e.target.value)}
              />
            ) : null}
          </label>
          <label className="checkbox-inline">
            <input
              type="radio"
              checked={!enabled}
              onChange={() => onEnabledChange(false)}
            />
            否
          </label>
        </div>
      </div>
    );
  }

  function renderEditBody() {
    if (activeTab === 'language') {
      return (
        <>
          <p className="front-config-view__label">前台語系：</p>
          <DualListTransfer
            enabledKeys={languageForm.enabledKeys}
            allItems={languageItems}
            onChange={(enabledKeys) => setLanguageForm({ enabledKeys })}
          />
          <p className="front-config-hint">＊排序第一位為預設值</p>
        </>
      );
    }

    if (activeTab === 'register') {
      return (
        <div className="settings-form">
          <SwitchRadio
            label="未登入只可以看到註冊/登入頁"
            value={registerForm.showOnlyAuthPage}
            onChange={(value) => setRegisterForm((prev) => ({ ...prev, showOnlyAuthPage: value }))}
          />
          <SwitchRadio
            label="登入/註冊頁是否顯示切換語系 UI"
            value={registerForm.showLanguageSwitchUI}
            onChange={(value) => setRegisterForm((prev) => ({ ...prev, showLanguageSwitchUI: value }))}
          />
          <BoolRadio
            label="註冊成功時是否綁定試玩帳號"
            value={registerForm.bindTouristAccountOnRegister}
            onChange={(value) => setRegisterForm((prev) => ({ ...prev, bindTouristAccountOnRegister: value }))}
          />
          <section className="settings-section">
            <h4>登入設置</h4>
            <div className="checkbox-row">
              <label className="checkbox-inline">
                <input
                  type="checkbox"
                  checked={registerForm.emailLogin}
                  onChange={(e) => setRegisterForm((prev) => ({ ...prev, emailLogin: e.target.checked }))}
                />
                email
              </label>
              <label className="checkbox-inline">
                <input
                  type="checkbox"
                  checked={registerForm.phoneLogin}
                  onChange={(e) => setRegisterForm((prev) => ({ ...prev, phoneLogin: e.target.checked }))}
                />
                手機號碼
              </label>
            </div>
            <BoolRadio
              label="登入頁面是否顯示忘記密碼"
              value={registerForm.forgotPassword}
              onChange={(value) => setRegisterForm((prev) => ({ ...prev, forgotPassword: value }))}
            />
          </section>
          <section className="settings-section">
            <h4>註冊設置</h4>
            <div className="checkbox-row">
              <label className="checkbox-inline">
                <input
                  type="checkbox"
                  checked={registerForm.emailRegister}
                  onChange={(e) => setRegisterForm((prev) => ({ ...prev, emailRegister: e.target.checked }))}
                />
                email
              </label>
              <label className="checkbox-inline">
                <input
                  type="checkbox"
                  checked={registerForm.phoneRegister}
                  onChange={(e) => setRegisterForm((prev) => ({ ...prev, phoneRegister: e.target.checked }))}
                />
                手機號碼
              </label>
            </div>
            <SwitchRadio
              label="註冊需要邀請碼"
              value={registerForm.needPromotionCode}
              onChange={(value) => setRegisterForm((prev) => ({ ...prev, needPromotionCode: value }))}
            />
            <SwitchRadio
              label="註冊需顯示交易密碼"
              value={registerForm.registerTradePassword}
              onChange={(value) => setRegisterForm((prev) => ({ ...prev, registerTradePassword: value }))}
            />
            <p className="front-config-view__label">可註冊/登入的國碼：</p>
            <DualListTransfer
              enabledKeys={registerForm.enabledCountryKeys}
              allItems={countryItems}
              onChange={(enabledCountryKeys) => setRegisterForm((prev) => ({
                ...prev,
                enabledCountryKeys,
              }))}
            />
            <p className="front-config-hint">＊排序第一位為預設值</p>
            <SwitchRadio
              label="遊客試玩開關"
              value={registerForm.tourists}
              onChange={(value) => setRegisterForm((prev) => ({ ...prev, tourists: value }))}
            />
            <div className="form-field form-field--full">
              <span className="form-field__label">註冊成功是否贈送彩金</span>
              <div className="product-type-radios">
                <label className="checkbox-inline">
                  <input
                    type="radio"
                    checked={registerForm.sendBonusOnRegister}
                    onChange={() => setRegisterForm((prev) => ({ ...prev, sendBonusOnRegister: true }))}
                  />
                  是，贈送彩金
                </label>
                <label className="checkbox-inline">
                  <input
                    type="radio"
                    checked={!registerForm.sendBonusOnRegister}
                    onChange={() => setRegisterForm((prev) => ({ ...prev, sendBonusOnRegister: false }))}
                  />
                  否
                </label>
              </div>
              {registerForm.sendBonusOnRegister ? (
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={registerForm.sendBonusWithRegister}
                  onChange={(e) => setRegisterForm((prev) => ({
                    ...prev,
                    sendBonusWithRegister: e.target.value,
                  }))}
                />
              ) : null}
            </div>
          </section>
        </div>
      );
    }

    if (activeTab === 'kycPayment') {
      return (
        <div className="settings-form">
          <section className="settings-section">
            <p className="front-config-view__label">認證資訊選項：</p>
            <DualListTransfer
              enabledKeys={kycPaymentForm.usedList}
              allItems={KYC_FIELD_OPTIONS.map((item) => ({ key: item.value, label: item.label }))}
              onChange={(usedList) => setKycPaymentForm((prev) => ({ ...prev, usedList }))}
            />
            <BoolRadio
              label="提現是否須 KYC"
              value={kycPaymentForm.withdrawNeedKyc}
              onChange={(value) => setKycPaymentForm((prev) => ({ ...prev, withdrawNeedKyc: value }))}
            />
            <BoolRadio
              label="綁定地址是否需要 KYC"
              value={kycPaymentForm.bindWithdrawAddressNeedKyc}
              onChange={(value) => setKycPaymentForm((prev) => ({ ...prev, bindWithdrawAddressNeedKyc: value }))}
            />
            <SwitchRadio
              label="尚有未審核訂單，不得提現"
              value={kycPaymentForm.blockWithdrawWhenPending}
              onChange={(value) => setKycPaymentForm((prev) => ({ ...prev, blockWithdrawWhenPending: value }))}
            />
            <SwitchRadio
              label="提現閥值開關"
              value={kycPaymentForm.withdrawalThresholdSwitch}
              onChange={(value) => setKycPaymentForm((prev) => ({ ...prev, withdrawalThresholdSwitch: value }))}
            />
            {kycPaymentForm.withdrawalThresholdSwitch ? (
              <div className="form-grid">
                <div className="form-field">
                  <label htmlFor="minWithdrawal">最低提現餘額</label>
                  <input
                    id="minWithdrawal"
                    type="number"
                    value={kycPaymentForm.minWithdrawal}
                    onChange={(e) => setKycPaymentForm((prev) => ({ ...prev, minWithdrawal: e.target.value }))}
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="minWithdrawalBalancePercent">最低提現餘額百分比</label>
                  <input
                    id="minWithdrawalBalancePercent"
                    type="number"
                    value={kycPaymentForm.minWithdrawalBalancePercent}
                    onChange={(e) => setKycPaymentForm((prev) => ({
                      ...prev,
                      minWithdrawalBalancePercent: e.target.value,
                    }))}
                  />
                </div>
              </div>
            ) : null}
            <BoolRadio
              label="是否充值自動鎖倉"
              value={kycPaymentForm.depositAutoLock}
              onChange={(value) => setKycPaymentForm((prev) => ({ ...prev, depositAutoLock: value }))}
            />
            <div className="form-field form-field--full">
              <span className="form-field__label">測試會員是否開放前台提現及自動審核</span>
              <div className="product-type-radios">
                {TEST_MEMBER_WITHDRAW_OPTIONS.map((option) => (
                  <label key={option.value} className="checkbox-inline">
                    <input
                      type="radio"
                      checked={kycPaymentForm.testMemberWithdraw === option.value}
                      onChange={() => setKycPaymentForm((prev) => ({
                        ...prev,
                        testMemberWithdraw: option.value,
                      }))}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>
            <BoolRadio
              label="測試會員是否開放前台存款自動審核"
              value={kycPaymentForm.autoApproveTestMemberDeposit}
              onChange={(value) => setKycPaymentForm((prev) => ({
                ...prev,
                autoApproveTestMemberDeposit: value,
              }))}
            />
            <BoolRadio
              label="首次取款是否檢核充值達到指定金額"
              value={kycPaymentForm.checkFirstWithdrawWalletRange}
              onChange={(value) => setKycPaymentForm((prev) => ({
                ...prev,
                checkFirstWithdrawWalletRange: value,
              }))}
            />
          </section>
        </div>
      );
    }

    if (activeTab === 'verifyCode') {
      return (
        <div className="settings-form">
          {VERIFY_CODE_OPTIONS.map((option) => (
            <div key={option.key} className="form-field form-field--full">
              <span className="form-field__label">{option.label}</span>
              <div className="product-type-radios">
                {option.mode === 'binary' ? (
                  <>
                    <label className="checkbox-inline">
                      <input
                        type="radio"
                        checked={verifyCodeForm.modes[option.key] === 'enabled'}
                        onChange={() => setVerifyCodeForm((prev) => ({
                          ...prev,
                          modes: { ...prev.modes, [option.key]: 'enabled' },
                        }))}
                      />
                      啟用
                    </label>
                    <label className="checkbox-inline">
                      <input
                        type="radio"
                        checked={verifyCodeForm.modes[option.key] !== 'enabled'}
                        onChange={() => setVerifyCodeForm((prev) => ({
                          ...prev,
                          modes: { ...prev.modes, [option.key]: 'disabled' },
                        }))}
                      />
                      停用
                    </label>
                  </>
                ) : (
                  <>
                    <label className="checkbox-inline">
                      <input
                        type="radio"
                        checked={verifyCodeForm.modes[option.key] === 'enabled'}
                        onChange={() => setVerifyCodeForm((prev) => ({
                          ...prev,
                          modes: { ...prev.modes, [option.key]: 'enabled' },
                        }))}
                      />
                      啟用
                    </label>
                    <label className="checkbox-inline">
                      <input
                        type="radio"
                        checked={verifyCodeForm.modes[option.key] === 'direct'}
                        onChange={() => setVerifyCodeForm((prev) => ({
                          ...prev,
                          modes: { ...prev.modes, [option.key]: 'direct' },
                        }))}
                      />
                      停用，直接輸入新密碼
                    </label>
                    <label className="checkbox-inline">
                      <input
                        type="radio"
                        checked={verifyCodeForm.modes[option.key] === 'need_old'}
                        onChange={() => setVerifyCodeForm((prev) => ({
                          ...prev,
                          modes: { ...prev.modes, [option.key]: 'need_old' },
                        }))}
                      />
                      停用，需輸入舊密碼
                    </label>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (activeTab === 'logo') {
      const fields = [
        { key: 'tenantName', label: '前台站台名稱', type: 'text' },
        { key: 'icon', label: '前台 favicon (32*32)', type: 'image' },
        { key: 'backendSiteName', label: '後台站台名稱', type: 'text' },
        { key: 'backendSiteIcon', label: '後台 favicon (32*32)', type: 'image' },
        { key: 'banner', label: '品牌 logo (160*160)', type: 'image' },
        { key: 'email', label: '電郵 logo (744*160)', type: 'image' },
      ];
      return (
        <div className="settings-form">
          {fields.map((field) => (
            <div key={field.key} className="form-field form-field--full">
              <label htmlFor={`logo-${field.key}`}>{field.label}</label>
              {field.type === 'text' ? (
                <input
                  id={`logo-${field.key}`}
                  type="text"
                  value={logoForm[field.key]}
                  onChange={(e) => setLogoForm((prev) => ({ ...prev, [field.key]: e.target.value }))}
                />
              ) : (
                <div className="front-config-logo-upload">
                  {logoForm[field.key] ? (
                    <img src={resolveAdminMediaUrl(logoForm[field.key])} alt="" className="front-config-logo-preview" />
                  ) : null}
                  <label className="btn btn-primary btn-sm">
                    {uploadingField === field.key ? '上傳中…' : `上傳 ${field.label}`}
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      disabled={uploadingField === field.key}
                      onChange={(e) => handleLogoUpload(field.key, e)}
                    />
                  </label>
                </div>
              )}
            </div>
          ))}
        </div>
      );
    }

    if (activeTab === 'timeZone') {
      return (
        <div className="settings-form">
          <div className="form-field form-field--full">
            <span className="form-field__label">時區</span>
            <p className="front-config-readonly-value">{timeZoneForm.timeZone || '—'}</p>
            <p className="front-config-hint front-config-hint--danger">
              ＊開站時確認，不得更動，避免開獎和資料出現問題
            </p>
          </div>
          <BoolRadio
            label="是否跟隨客戶端時區"
            value={timeZoneForm.useBrowserTimezone}
            onChange={(value) => setTimeZoneForm((prev) => ({ ...prev, useBrowserTimezone: value }))}
          />
          <div className="form-field form-field--full">
            <label htmlFor="dateFormat">日期格式</label>
            <select
              id="dateFormat"
              value={timeZoneForm.dateFormat}
              onChange={(e) => setTimeZoneForm((prev) => ({ ...prev, dateFormat: e.target.value }))}
            >
              {DATE_FORMAT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div className="form-field form-field--full">
            <label htmlFor="timeFormat">時間格式</label>
            <select
              id="timeFormat"
              value={timeZoneForm.timeFormat}
              onChange={(e) => setTimeZoneForm((prev) => ({ ...prev, timeFormat: e.target.value }))}
            >
              {TIME_FORMAT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
        </div>
      );
    }

    if (activeTab === 'global') {
      return (
        <div className="settings-form">
          <div className="form-field form-field--full">
            <label htmlFor="h5Url">免密登入前台域名</label>
            <input
              id="h5Url"
              type="text"
              value={globalForm.h5Url}
              onChange={(e) => setGlobalForm((prev) => ({ ...prev, h5Url: e.target.value }))}
            />
          </div>
          <BoolRadio
            label="全局投注頁是否顯示賠率"
            value={globalForm.showBettingOdds}
            onChange={(value) => setGlobalForm((prev) => ({ ...prev, showBettingOdds: value }))}
          />
          <BoolRadio
            label="明細漲跌符號是否顯示"
            value={globalForm.showDetailUpDownSign}
            onChange={(value) => setGlobalForm((prev) => ({ ...prev, showDetailUpDownSign: value }))}
          />
          <BoolRadio
            label="持倉明細是否顯示日期與現價"
            value={globalForm.showPositionDetailDateAndPrice}
            onChange={(value) => setGlobalForm((prev) => ({
              ...prev,
              showPositionDetailDateAndPrice: value,
            }))}
          />
          {renderConditionalAmountRadio({
            label: '是否限制餘額大於指定金額才可投注',
            enabled: globalForm.betAmountSwitch,
            amount: globalForm.betAmount,
            onEnabledChange: (value) => setGlobalForm((prev) => ({ ...prev, betAmountSwitch: value })),
            onAmountChange: (value) => setGlobalForm((prev) => ({ ...prev, betAmount: value })),
          })}
          <BoolRadio
            label="登入、註冊、語言設定頁是否顯示國家國旗"
            value={globalForm.showCountryFlag}
            onChange={(value) => setGlobalForm((prev) => ({ ...prev, showCountryFlag: value }))}
          />
          <BoolRadio
            label="是否開放前台交割合約、永續合約撤單功能"
            value={globalForm.enableContractCancel}
            onChange={(value) => setGlobalForm((prev) => ({ ...prev, enableContractCancel: value }))}
          />
          <div className="form-field form-field--full">
            <span className="form-field__label">
              交割合約當日投注次數大於等於
              <input
                type="number"
                min="0"
                step="1"
                className="inline-number-input"
                value={globalForm.deliveriesNumber}
                onChange={(e) => setGlobalForm((prev) => ({
                  ...prev,
                  deliveriesNumber: e.target.value,
                }))}
              />
              ，用戶餘額百分比
              <input
                type="number"
                min="0"
                step="any"
                className="inline-number-input"
                value={globalForm.deliveriesAmountProportion}
                onChange={(e) => setGlobalForm((prev) => ({
                  ...prev,
                  deliveriesAmountProportion: e.target.value,
                }))}
              />
              %，視為違規利潤
            </span>
          </div>
          <BoolRadio
            label="投注頁是否可以手動輸入金額"
            value={globalForm.allowManualAmountInput}
            onChange={(value) => setGlobalForm((prev) => ({ ...prev, allowManualAmountInput: value }))}
          />
          <BoolRadio
            label="不同站點註冊帳號是否允許重複"
            value={globalForm.checkUsernameRegisterDomainEnable}
            onChange={(value) => setGlobalForm((prev) => ({
              ...prev,
              checkUsernameRegisterDomainEnable: value,
            }))}
          />
          <div className="form-field form-field--full">
            <label htmlFor="checkUsernameRegisterDomains">域名設置</label>
            <textarea
              id="checkUsernameRegisterDomains"
              rows={3}
              value={globalForm.checkUsernameRegisterDomains}
              onChange={(e) => setGlobalForm((prev) => ({
                ...prev,
                checkUsernameRegisterDomains: e.target.value,
              }))}
            />
          </div>
          <div className="form-field form-field--full">
            <span className="form-field__label">
              資金明細卡片，是否顯示自定義幣種、鎖倉挖礦、鎖倉金額、提款統計
            </span>
            <div className="checkbox-row">
              {GLOBAL_WALLET_TYPE_OPTIONS.map((option) => (
                <label key={option.value} className="checkbox-inline">
                  <input
                    type="checkbox"
                    checked={globalForm.globalWalletTypes.includes(option.value)}
                    onChange={(e) => setGlobalForm((prev) => {
                      const next = new Set(prev.globalWalletTypes);
                      if (e.target.checked) {
                        next.add(option.value);
                      } else {
                        next.delete(option.value);
                      }
                      return { ...prev, globalWalletTypes: [...next] };
                    })}
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </div>
          <SwitchRadio
            label="首頁是否顯示活動專區懸浮 icon"
            value={globalForm.showActivityIcon}
            onChange={(value) => setGlobalForm((prev) => ({ ...prev, showActivityIcon: value }))}
          />
          {renderConditionalAmountRadio({
            label: '是否限制鎖倉金額低於指定金額無法跟單',
            enabled: globalForm.followOrderLockedBalanceRestrictOpen,
            amount: globalForm.followOrderLockedBalanceRestrictAmount,
            onEnabledChange: (value) => setGlobalForm((prev) => ({
              ...prev,
              followOrderLockedBalanceRestrictOpen: value,
            })),
            onAmountChange: (value) => setGlobalForm((prev) => ({
              ...prev,
              followOrderLockedBalanceRestrictAmount: value,
            })),
          })}
        </div>
      );
    }

    return null;
  }

  function renderActiveView() {
    if (activeTab === 'language') {
      return renderLanguageView();
    }
    if (activeTab === 'register') {
      return renderRegisterView();
    }
    if (activeTab === 'kycPayment') {
      return renderKycPaymentView();
    }
    if (activeTab === 'verifyCode') {
      return renderVerifyCodeView();
    }
    if (activeTab === 'logo') {
      return renderLogoView();
    }
    if (activeTab === 'timeZone') {
      return renderTimeZoneView();
    }
    if (activeTab === 'global') {
      return renderGlobalView();
    }
    return null;
  }

  return (
    <div className="page-card front-config-page">
      <div className="section-header">
        <h2>前台管理</h2>
        <button type="button" className="btn btn-primary" disabled={loading} onClick={openEdit}>
          修改
        </button>
      </div>

      <nav className="product-tabs" aria-label="前台管理分頁">
        {FRONT_CONFIG_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`product-tabs__item ${activeTab === tab.key ? 'is-active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {loading ? <p>載入中…</p> : null}
      {error ? <div className="alert alert-error">{error}</div> : null}
      {message ? <div className="alert alert-success">{message}</div> : null}
      {!loading ? renderActiveView() : null}

      <FrontConfigEditModal
        open={editOpen}
        title="修改"
        submitting={submitting}
        onSubmit={handleSubmit}
        onReset={handleReset}
        onClose={() => setEditOpen(false)}
      >
        {renderEditBody()}
      </FrontConfigEditModal>
    </div>
  );
}
